const locale = typeof translation_locale !== "undefined" ? translation_locale : 'en';
const messages = typeof translation_messages !== "undefined" ? translation_messages : {};

/**
 * Get the current locale.
 * @return {string} The current locale.
 */
export function getLocale() {
    return locale;
}

/**
 * Formats numbers according to the current locale.
 * @param {number} number - The number to format.
 * @param {object} options - Optional Intl.NumberFormat options.
 * @returns {string} The formatted number.
 */
export function formatNumber(number, options = {}) {
    const locale = getLocale();
    const defaultOptions = {
        useGrouping: true,
        minimumFractionDigits: 0,
    };

    return new Intl.NumberFormat(locale, { ...defaultOptions, ...options }).format(number);
}

function getKeyWithoutGroup(key) {
    return key.includes(".") ? key.substring(key.indexOf(".") + 1) : key;
}

export function trans(key, replacements = {}, linkAdditionalClasses = '') {
    try {
        let message = key;
        let translationFound = false;

        if (has(key)) {
            message = _getMessage(key);
            if (message !== null) {
                translationFound = true;
            } else {
                message = key;
            }
        }

        if (replacements) {
            message = _applyReplacements(message, replacements);
            message = _processTranslationLinks(message, replacements, linkAdditionalClasses);
        }

        // Strip group prefix when appropriate
        if (!translationFound) {
            const stringParts = message.match(/^(?:([a-zA-Z]{2,})\.(?! ))?(.+)$/s);
            if (stringParts && stringParts.length === 3) {
                return stringParts[2];
            }
        }

        return message;
    } catch (error) {
        console.error("i18n.js->trans(): Error when translating key:", key, error);
        return getKeyWithoutGroup(key);
    }
}

/**
 * Returns true if the key is defined on the messages source.
 * @param key {string} The key of the message.
 * @return {boolean} true if the given key is defined on the messages source, otherwise false.
 */
export function has(key) {
    if (typeof key !== 'string' || !messages) {
        return false;
    }
    return _getMessage(key) !== null;
}

/**
 * Gets the plural or singular form of the message specified based on an integer value.
 * @param key {string} The key of the message.
 * @param count {number} The number of elements.
 * @param replacements {object} The replacements to be done in the message.
 * @return {string} The translation message according to an integer value.
 */
export function choice(key, count, replacements = {}) {
    replacements.count = count;
    const message = trans(key, replacements);
    const parts = message.split('|').map(s => s.trim());
    if (parts.length === 1) return message;
    return count > 1 ? parts[1] : parts[0];
}

/**
 * Parse a message key into components.
 * @param {string} key Translation key to parse. Could be in format with group ("payments.Payment was successfully processed") or without ("Thank you")
 * @return {{group: string, text: string}|null}
 */
function _parseKey(key) {
    if (typeof key !== 'string') {
        return null;
    }

    const regex = /^(?<group>[a-zA-Z]+)\.(?<text>.+)$/;
    let match = regex.exec(key);

    // No match -> `key` doesn't contain group, return as is and add group "undefined"
    if (!match) {
        return { group: "undefined", text: key.trim() };
    }

    const { group, text } = match.groups;
    return {
        group: group,
        text: text
    };
}

/**
 * Returns a translation message. Use `trans()` method instead, this methods assumes the key exists.
 * @param {string} key The key of the message.
 * @return {string|null} The translation message for the given key.
 */
function _getMessage(key) {
    let parsedKey = _parseKey(key);

    if (!parsedKey) {
        return null;
    }

    return messages?.[parsedKey.group]?.[parsedKey.text] ?? null;
}

/**
 * Apply replacements to a string message containing placeholders.
 * @param message {string} The text message.
 * @param replacements {object} The replacements to be done in the message.
 * @return {string} The string message with replacements applied.
 */
function _applyReplacements(message, replacements) {
    message = String(message);
    for (let replace in replacements) {
        if (Object.prototype.hasOwnProperty.call(replacements, replace)) {
        message = message.split(`:${replace}`).join(replacements[replace]);
        }
    }
    return message;
}

/**
 * Process translation strings to replace link placeholders with HTML links.
 *
 * This function looks for placeholders in the format: [text|link]
 * Where 'link' is a key in the replacements object. It replaces these placeholders with HTML links.
 * 
 * Exception: if you use same linkText and linkKey, a link url will be used as link text.
 *
 * NOTE: The same function pattern is also used in PHP translations in helpers.php->processTranslationLinks(). If you change it here, change it there too.
 *
 * @param {string} message The translation message
 * @param {object} replacements The replacements object containing link URLs
 * @param {string} linkAdditionalClasses
 * @return {string} The message with link placeholders replaced by HTML links
 */
function _processTranslationLinks(message, replacements, linkAdditionalClasses) {
    message = String(message);
    return message.replace(/\[([^\]]+?)\|([a-zA-Z0-9_-]+)\]/g, (match, linkText, linkKey) => {
        if (Object.prototype.hasOwnProperty.call(replacements, linkKey)) {
            const url = replacements[linkKey];
            const escapedUrl = url.replace(/"/g, '&quot;').replace(/'/g, '&#39;').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
            let escapedLinkText = linkText.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
            if (linkText === linkKey) escapedLinkText = escapedUrl;
            return `<a href="${escapedUrl}" class="vincario-link ${linkAdditionalClasses}">${escapedLinkText}</a>`;
        }
        return match;
    });
}
